import { useCallback, useState, useMemo } from 'react'
import { useExplorer } from '../../../../../context/explorer'

/**
 * Hook for DataTab functionality.
 * Uses ExplorerContext for state management instead of local state.
 *
 * Key changes from old implementation:
 * - No longer maintains local selectedTreeItems state
 * - Derives selection state from dataConfig.selectedItems in context
 * - No longer manages dialog state (moved to Modal Manager)
 * - Provides helper functions for tree operations
 */
export const useExplorerDataTab = () => {
    // Get state and actions from context
    const { dataConfig, workspaceMetadata, workspaceLoading } = useExplorer()

    // Local UI state for search only
    const [searchTerm, setSearchTerm] = useState('')

    /**
     * Counts selected items within a tree node.
     * Derives count from dataConfig.selectedItems (context) instead of local state.
     */
    const countSelectedInTree = useCallback(
        (node) => {
            if (!node) return 0

            let count = 0

            // Check if this node is a selected column
            if (node.type === 'column') {
                const isSelected = dataConfig.selectedItems.some(
                    (item) =>
                        item.measureName === node.measureName &&
                        item.reportSeriesTableID === node.reportSeriesTableID &&
                        !item.metricKey // Exclude saved KPIs
                )
                if (isSelected) count++
            }

            // Recursively count children
            if (node.children) {
                count += node.children.reduce((acc, child) => acc + countSelectedInTree(child), 0)
            }

            return count
        },
        [dataConfig.selectedItems]
    )

    /**
     * Checks if a specific column node is selected.
     * @param {Object} node - The column node to check
     * @returns {boolean} - Whether the column is selected
     */
    const isColumnSelected = useCallback(
        (node) => {
            return dataConfig.selectedItems.some(
                (item) =>
                    item.measureName === node.measureName &&
                    item.reportSeriesTableID === node.reportSeriesTableID &&
                    !item.metricKey
            )
        },
        [dataConfig.selectedItems]
    )

    /**
     * Finds the index of a column in selectedItems.
     * @param {Object} node - The column node to find
     * @returns {number} - Index in selectedItems or -1 if not found
     */
    const findSelectedIndex = useCallback(
        (node) => {
            return dataConfig.selectedItems.findIndex(
                (item) =>
                    item.measureName === node.measureName &&
                    item.reportSeriesTableID === node.reportSeriesTableID &&
                    !item.metricKey
            )
        },
        [dataConfig.selectedItems]
    )

    /**
     * Structures inbox data as a tree for the TreeView component.
     */
    const structureDataAsTree = useCallback((datasets) => {
        if (!datasets || !Array.isArray(datasets)) return null

        const treeData = {
            id: 'root',
            name: 'Datasets',
            children: datasets
                .map((dataset) => {
                    const workspaceId = dataset.workspaceID
                    const inboxID = dataset.inboxID
                    const datasetNode = {
                        id: dataset.inboxID,
                        name: dataset.label || dataset.inboxName,
                        type: 'dataset',
                        workspaceID: workspaceId,
                    }
                    if (dataset.tables && dataset.tables.length > 0) {
                        datasetNode.children = dataset.tables.map((table) => {
                            const reportSeriesTableID = table.reportSeriesTableID
                            const tableNode = {
                                id: reportSeriesTableID,
                                name: table.tableName,
                                type: 'table',
                                workspaceID: workspaceId,
                                reportSeriesTableID: reportSeriesTableID,
                            }

                            if (table.columns && table.columns.length > 0) {
                                tableNode.children = table.columns
                                    .map((column, index) => {
                                        if (!column.isMeasure) return null
                                        return {
                                            id: `${reportSeriesTableID}_col_${index}`,
                                            name: column.columnName,
                                            type: 'column',
                                            workspaceID: workspaceId,
                                            reportSeriesTableID: reportSeriesTableID,
                                            measureName: column.columnName,
                                            format: {
                                                formatString: column.format,
                                                format: column.format,
                                            },
                                            inboxID: inboxID,
                                        }
                                    })
                                    .filter(Boolean)
                            }
                            return tableNode
                        })
                    }
                    // In case that the dataset has no tables we dont display it
                    return Boolean(datasetNode?.children) ? datasetNode : null
                })
                .filter(Boolean),
        }
        return treeData
    }, [])

    /**
     * Filters tree data based on search term.
     * Only matches column nodes (not datasets or tables).
     */
    const filteredTreeData = useCallback((treeData, searchTerm) => {
        if (!searchTerm || !treeData) return treeData

        const normalizedSearch = searchTerm.toLowerCase().trim()

        const filterNode = (node) => {
            if (!node) return null

            const normalizedNodeName = node.name.toLowerCase()
            const currentNodeMatches =
                node.type === 'column' && normalizedNodeName.includes(normalizedSearch)

            if (currentNodeMatches) {
                return {
                    ...node,
                    children: node.children || [],
                }
            }

            if (node.children && node.children.length > 0) {
                const filteredChildren = node.children
                    .map((child) => filterNode(child))
                    .filter(Boolean)
                if (filteredChildren.length > 0) {
                    return {
                        ...node,
                        children: filteredChildren,
                    }
                }
            }

            return null
        }

        return filterNode(treeData)
    }, [])

    /**
     * Gets expanded node IDs for search results.
     * Expands all parent nodes when searching.
     */
    const getExpandedNodes = useCallback((treeData) => {
        if (!treeData) return []

        const expanded = []
        const traverse = (node) => {
            if (!node) return
            if (node.children && node.children.length > 0) {
                expanded.push(node.id)
                node.children.forEach(traverse)
            }
        }
        traverse(treeData)
        return expanded
    }, [])

    // Memoized tree data from workspace metadata
    const treeData = useMemo(() => {
        if (workspaceLoading || !workspaceMetadata?.inboxes) return null
        return structureDataAsTree(workspaceMetadata.inboxes)
    }, [workspaceMetadata?.inboxes, workspaceLoading, structureDataAsTree])

    // Filtered tree data based on search
    const displayTreeData = useMemo(() => {
        if (!treeData) return null
        return searchTerm ? filteredTreeData(treeData, searchTerm) : treeData
    }, [treeData, searchTerm, filteredTreeData])

    // Expanded nodes for search
    const expandedNodes = useMemo(() => {
        if (!searchTerm || !displayTreeData) return []
        return getExpandedNodes(displayTreeData)
    }, [searchTerm, displayTreeData, getExpandedNodes])

    return {
        // Tree data
        treeData,
        displayTreeData,
        expandedNodes,

        // Search
        searchTerm,
        setSearchTerm,

        // Selection helpers (derived from context)
        countSelectedInTree,
        isColumnSelected,
        findSelectedIndex,

        // Tree utilities
        structureDataAsTree,
        filteredTreeData,
        getExpandedNodes,
    }
}
